/**
 * Estado global "la sesión dejó de servir", fuera de React a propósito.
 *
 * <p>Lo escriben dos clases de avisos —los eventos del {@code UserManager} de OIDC y el 401 de la
 * API— y lo leen tres clases de consumidores: el aviso que ve el usuario, los pollers que tienen
 * que frenar, y los tests.
 *
 * <p><b>Por qué no es un contexto de React.</b> El escritor principal es
 * {@code useApiClient}, que consumen 12 servicios y ~89 call sites, y que más de 30 suites
 * renderizan <b>sin ningún provider</b>. Un contexto obligaría a envolver todas esas suites y a
 * sumar dependencias a los {@code useCallback} del hook. Un import plano no le cambia la identidad a
 * nada y funciona igual desde un {@code setInterval} que desde un componente.
 *
 * <p><b>La idempotencia no es un lujo.</b> El incidente que originó esto (#168) fueron 9414
 * requests fallidas del mismo token en 70 minutos. Sin la guarda, cada una notificaría y el árbol de
 * React se re-renderizaría 9414 veces mientras el usuario mira una pantalla que no responde.
 */

/** Motivos válidos, en orden de cercanía a la causa. */
export const SESSION_EXPIRY_REASONS = Object.freeze({
  /** El {@code UserManager} avisó que el access token venció. */
  TOKEN_EXPIRED: 'token-expired',
  /** La renovación se intentó y falló, ya agotados los reintentos. */
  RENEW_FAILED: 'renew-failed',
  /** La API respondió 401. Puede no ser vencimiento: ver el copy de contingencia del aviso. */
  API_401: 'api-401',
});

const MOTIVOS = Object.values(SESSION_EXPIRY_REASONS);

/** {@code null} mientras la sesión sirve; el motivo, cuando dejó de servir. */
let motivo = null;

const suscriptores = new Set();

const notificar = () => {
  // Se copia el Set antes de recorrerlo: un suscriptor puede desuscribirse desde su propio
  // callback (el modal lo hace al desmontarse) y mutar la colección durante la iteración.
  [...suscriptores].forEach((cb) => {
    try {
      cb(motivo);
    } catch (err) {
      // Un suscriptor roto no puede dejar sin aviso a los demás: el que se cae podría ser un
      // poller y el que se queda sin enterarse, el aviso que ve el usuario.
      console.error('[sessionExpiry] Un suscriptor falló al ser notificado:', err);
    }
  });
};

/** {@code true} si la sesión dejó de servir. */
export const isSessionExpired = () => motivo !== null;

/** El motivo por el que dejó de servir, o {@code null}. */
export const sessionExpiryReason = () => motivo;

/**
 * Registra un callback y <b>lo invoca de inmediato con el estado actual</b>.
 *
 * <p>La invocación inicial no es cortesía: el 401 puede marcar la sesión antes de que monte el
 * componente que muestra el aviso, y sin ella ese aviso no aparecería nunca justo en el caso más
 * rápido.
 *
 * @return la función para desuscribirse, segura de llamar más de una vez.
 */
export const subscribeSessionExpired = (cb) => {
  suscriptores.add(cb);
  try {
    cb(motivo);
  } catch (err) {
    console.error('[sessionExpiry] Un suscriptor falló al recibir el estado inicial:', err);
  }
  return () => { suscriptores.delete(cb); };
};

/**
 * Marca la sesión como vencida. <b>Idempotente</b>: la primera marca gana y las siguientes no
 * notifican, así que se puede llamar desde cada respuesta 401 sin pensarlo.
 */
export const markSessionExpired = (reason) => {
  if (!MOTIVOS.includes(reason)) {
    throw new Error(`[sessionExpiry] Motivo desconocido: ${reason}. Válidos: ${MOTIVOS.join(', ')}`);
  }
  if (motivo !== null) return;
  motivo = reason;
  notificar();
};

/**
 * Vuelve al estado sano. Lo llama el evento {@code addUserLoaded}: si una renovación entra bien
 * después de que un 401 marcara la sesión, el aviso tiene que irse solo.
 */
export const resetSessionExpiry = () => {
  if (motivo === null) return;
  motivo = null;
  notificar();
};
